import { Box, Button, Paper, TextField, Typography } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import { Map } from 'immutable';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContactSelector } from '@sophocles/components/contacts';
import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import IssueCategorySelectComponent from '@sophocles/components/IssueCategorySelectComponent';

// Filled multiline pads the root, not the textarea, so a short value centers
// vertically in the tall slot unless anchored to the top; see
// SettingsTextField for the fuller writeup.
const multilineSx = {
  '& .MuiInputBase-root.MuiInputBase-multiline': { alignItems: 'flex-start' },
  '& .MuiInputBase-inputMultiline': { alignSelf: 'flex-start', padding: '0 !important' },
};

export default function EditTicketForm({ ticket, onSubmit, onCancel, errorMessages }) {
  const { t } = useTranslation();

  const initialIssue = ticket.issue ?? null;
  const initialContact = ticket.contact || ticket.new_contact || null;
  const customer = ticket.customer ?? null;
  const siteGroup = ticket.site_group ?? null;
  const site = ticket.site ?? null;
  const createdBy = ticket.created_by ?? null;
  const ticketNumber = ticket.ticket_number ?? '';
  const ticketId = ticket.id ?? null;

  const [issueCategory, setIssueCategory] = useState(_.get(initialIssue, ['category', 'id'], 4));
  const [issueSubCategory, setIssueSubCategory] = useState(_.get(initialIssue, 'id', 2));
  const [selectedContact, setSelectedContact] = useState(initialContact || null);

  const initialValues = {
    reported_by: ticket.reported_by || '',
    problem_description: ticket.problem_description || '',
    resolution: ticket.resolution || '',
    internal_notes: ticket.internal_notes || '',
  };

  const handleSubmit = values => {
    onSubmit(ticketId, {
      ...values,
      issue_id: issueSubCategory,
      contact_id: selectedContact?.id ?? null,
      severity: 10,
    });
  };

  const handleIssueCategoryChange = (category, subCategory) => {
    setIssueCategory(category);
    setIssueSubCategory(subCategory);
  };

  const customerId = _.get(customer, 'id');
  const siteGroupId = _.get(siteGroup, 'id');

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', overflow: 'auto' }}>
      <Box sx={{ flex: '0 1 700px', width: '100%' }}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
            {({ submitForm, isSubmitting }) => (
              <Form>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t('tickets.ticketDetails', 'Ticket Details')}
                </Typography>
                <Box component="ul" sx={{ listStyle: 'none', p: 0, mb: 2 }}>
                  <li>
                    {t('tickets.ticketNumber', 'Ticket Number')}: <b>{ticketNumber}</b>
                  </li>
                  {customer ? (
                    <li>
                      {t('general.customer', 'Customer')}:{' '}
                      <span>
                        {_.get(customer, 'id', 0)}. {_.get(customer, 'name', 'none')}
                      </span>
                    </li>
                  ) : null}
                  {siteGroup ? (
                    <li>
                      {t('general.plant', 'Plant')}: {_.get(siteGroup, 'name', '0')}.{' '}
                      {_.get(siteGroup, 'description', 'none')}
                    </li>
                  ) : null}
                  {site ? (
                    <li>
                      {t('general.site', 'Site')}: {_.get(site, 'name', '0')}
                    </li>
                  ) : null}
                  {createdBy ? (
                    <li>
                      {t('general.createdBy', 'Created by')}: {_.get(createdBy, 'name', '')}
                    </li>
                  ) : null}
                </Box>

                <Field name="reported_by">
                  {({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      margin="normal"
                      label={t('tickets.nameOfCaller', 'Name of Caller')}
                    />
                  )}
                </Field>

                {customerId ? (
                  <Box sx={{ my: 2 }}>
                    <ContactSelector
                      label={t('tickets.contactOptional', 'Contact (Optional)')}
                      value={selectedContact}
                      onChange={contact => setSelectedContact(contact || null)}
                      customerId={customerId}
                      siteGroupId={siteGroupId}
                      customer={customer ? Map(customer) : undefined}
                      siteGroup={siteGroup ? Map(siteGroup) : undefined}
                      showAddress={false}
                      required={false}
                      quickMode
                      requireCompleteAddress={false}
                      showDefaultAddresses={false}
                      primaryContext="none"
                      requiredFields={['name', 'email']}
                    />
                  </Box>
                ) : null}

                <Field name="problem_description">
                  {({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={3}
                      margin="normal"
                      label={t('tickets.problemDescription', 'Problem Description')}
                      sx={multilineSx}
                    />
                  )}
                </Field>

                <Box sx={{ mt: 2 }}>
                  <IssueCategorySelectComponent
                    category={issueCategory}
                    subCategory={issueSubCategory}
                    onChange={handleIssueCategoryChange}
                  />
                </Box>

                <Field name="resolution">
                  {({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={3}
                      margin="normal"
                      label={t('tickets.resolution', 'Resolution')}
                      sx={multilineSx}
                    />
                  )}
                </Field>

                <Field name="internal_notes">
                  {({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={3}
                      margin="normal"
                      label={t('tickets.internalComments', 'Internal Comments')}
                      sx={multilineSx}
                    />
                  )}
                </Field>

                <FormErrorAlert messages={errorMessages} sx={{ mt: 2 }} />

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    py: 2,
                    justifyContent: 'space-between',
                  }}
                >
                  <Button variant="contained" color="primary" disabled={isSubmitting} onClick={submitForm}>
                    {t('actions.saveChanges', 'Save Changes')}
                  </Button>
                  <Button variant="contained" color="secondary" onClick={onCancel}>
                    {t('actions.cancel', 'Cancel')}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Paper>
      </Box>
    </Box>
  );
}
